import { useCallback, useEffect, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { studies } from '@/api/endpoints';
import type { StudySummary, StudyType } from '@/types';
import { Icon } from '@/components/Icon';
import { useSortable, SortHeader } from '@/components/SortableTable';
import { Pagination, usePaginated } from '@/components/Pagination';
import { NewStudyModal } from '@/components/NewStudyModal';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';

const FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Wszystkie' },
  { value: 'ACTIVE', label: 'Aktywne' },
  { value: 'COMPLETED', label: 'Zakończone' },
];

function priorytetPill(p: string): string {
  if (p === 'CRITICAL') return 'pill-red';
  if (p === 'HIGH') return 'pill-amber';
  return '';
}

/** Dni do terminu: dodatnie = zostało, 0 = dziś, ujemne = po terminie. Liczone od północy dziś. */
function daysToDeadline(deadline: string): number {
  const dl = new Date(deadline);
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfDl = new Date(dl.getFullYear(), dl.getMonth(), dl.getDate());
  return Math.round((startOfDl.getTime() - startOfToday.getTime()) / (1000 * 3600 * 24));
}

function terminColor(deadline?: string): string {
  if (!deadline) return 'text-ink-500';
  const days = daysToDeadline(deadline);
  if (days < 0) return 'text-red-500 font-semibold';
  if (days <= 3) return 'text-amber-500 font-semibold';
  return 'text-ink-600';
}

const pluralDni = (n: number) => (n === 1 ? 'dzień' : 'dni');

/** Etykieta „ile dni do końca / po terminie" pod datą terminu. */
function terminLabel(deadline?: string): string | null {
  if (!deadline) return null;
  const d = daysToDeadline(deadline);
  if (d === 0) return 'dziś';
  if (d > 0) return `za ${d} ${pluralDni(d)}`;
  const n = -d;
  return `${n} ${pluralDni(n)} po terminie`;
}

/** Wskaźnik kompletności wymaganych dokumentów: pasek + licznik received/total + badge overdue. */
function RequiredDocsCell({ summary }: { summary: StudySummary }) {
  const total = summary.requiredDocsTotal;
  if (total == null || total === 0) {
    return <span className="text-ink-400 text-[11px]">—</span>;
  }
  const received = summary.requiredDocsReceived ?? 0;
  const overdue = summary.requiredDocsOverdue ?? 0;
  const pct = Math.round((received / total) * 100);
  const barColor = pct >= 100 ? 'bg-green-500'
                 : overdue > 0 ? 'bg-red-500'
                 : pct >= 50 ? 'bg-water-500'
                 : 'bg-amber-400';
  return (
    <div className="text-[11px]" title={`${received}/${total} otrzymano · ${overdue} przeterminowanych`}>
      <div className="flex items-center gap-1.5">
        <span className="font-mono">{received}/{total}</span>
        {overdue > 0 && (
          <span className="pill pill-red text-[10px]" title={`${overdue} pozycji po terminie`}>
            ⚠ {overdue}
          </span>
        )}
      </div>
      <div className="w-20 h-1 bg-ink-100 rounded-full mt-1 overflow-hidden">
        <div className={'h-full ' + barColor} style={{ width: pct + '%' }}/>
      </div>
    </div>
  );
}

export function StudiesList() {
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') ?? '';
  const type = params.get('type') ?? '';
  const [rows, setRows] = useState<StudySummary[] | null>(null);
  const [types, setTypy] = useState<StudyType[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [newStudyOpen, setNewStudyOpen] = useState(false);
  const [historicalOpen, setHistoricalOpen] = useState(false);
  const { user } = useAuth();
  const canCreate = user?.permissions?.includes('STUDY_CREATE') ?? false;
  const nav = useNavigate();

  useEffect(() => {
    studies.types().then(setTypy).catch(() => { /* nonfatal */ });
  }, []);

  useEffect(() => {
    setRows(null);
    setErr(null);
    studies.list(filter || undefined, type || undefined)
      .then(setRows)
      .catch(e => setErr(String(e)));
  }, [filter, type]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Opracowania</h1>
          <div className="text-ink-400 text-[13px]">Dokumentacje hydrogeologiczne i projekty</div>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <button onClick={() => setHistoricalOpen(true)} className="btn btn-secondary btn-sm"
                    title="Wprowadź istniejące opracowanie tylko pod pilnowanie terminu (bez workflow)">
              <Icon name="plus" className="w-3.5 h-3.5"/> Historyczny wpis
            </button>
            <button onClick={() => setNewStudyOpen(true)} className="btn btn-primary btn-sm"
                    title="Tworzenie poza ścieżką oferta→umowa/zlecenie">
              <Icon name="plus" className="w-3.5 h-3.5"/> Nowe opracowanie
            </button>
          </div>
        )}
      </header>

      <NewStudyModal
        open={newStudyOpen}
        onClose={() => setNewStudyOpen(false)}
        onCreated={(id) => { setNewStudyOpen(false); nav(`/studies/${id}`); }}
      />
      <NewStudyModal
        open={historicalOpen}
        historical
        onClose={() => setHistoricalOpen(false)}
        onCreated={(id) => { setHistoricalOpen(false); nav(`/studies/${id}`); }}
      />

      <div className="card">
        <div className="card-head">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 bg-ink-50 p-1 rounded-md">
              {FILTERS.map(f => (
                <button
                  key={f.value || 'all'}
                  onClick={() => setParam('filter', f.value)}
                  className={'btn btn-sm ' + (filter === f.value ? 'btn-primary' : 'btn-ghost')}
                >{f.label}</button>
              ))}
            </div>
            <SearchableSelect
              value={type}
              onChange={v => setParam('type', v)}
              className="btn-sm"
            >
              <option value="">Wszystkie rodzaje</option>
              {types.map(t => (
                <option key={t.code} value={t.code}>{t.abbreviation}-{t.code} — {t.name}</option>
              ))}
            </SearchableSelect>
          </div>
        </div>
        <div className="card-body !p-0">
          {err && <div className="text-red-500 p-4">Błąd: {err}</div>}
          {!rows && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {rows && rows.length === 0 && <div className="text-ink-400 p-4">Brak opracowań.</div>}
          {rows && rows.length > 0 && <StudiesTable rows={rows} onRowClick={id => nav(`/studies/${id}`)}/>}
        </div>
      </div>
    </div>
  );
}

type SortKey = 'number' | 'title' | 'type' | 'intake' | 'client' | 'status' | 'priority' | 'docs' | 'deadline' | 'createdAt';

function StudiesTable({ rows, onRowClick }: { rows: StudySummary[]; onRowClick: (id: number) => void }) {
  const accessor = useCallback((o: StudySummary, k: SortKey) => {
    switch (k) {
      case 'number': return o.number;
      case 'title': return o.title;
      case 'type': return `${o.typeAbbreviation}-${o.typeCode}`;
      case 'intake': return o.intakeName ?? '';
      case 'client': return o.clientName ?? '';
      case 'status': return o.statusName;
      case 'priority': return o.priority === 'CRITICAL' ? 0 : o.priority === 'HIGH' ? 1 : 2;
      case 'docs':
        // Sortuj po liczbie otrzymanych / total (wyższy %), null/0 na koniec
        if (!o.requiredDocsTotal) return -1;
        return (o.requiredDocsReceived ?? 0) / o.requiredDocsTotal;
      case 'deadline': return o.deadline ? new Date(o.deadline) : null;
      case 'createdAt': return o.createdAt ? new Date(o.createdAt) : null;
    }
  }, []);
  const { sorted, sort, onHeaderClick } = useSortable<StudySummary, SortKey>(rows, 'createdAt', 'desc', accessor);
  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(sorted, { storageKey: 'studies' });

  return (
    <>
    <table className="table-hw">
      <thead>
        <tr>
          <SortHeader<SortKey> label="Numer"        sortKey="number"    current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Tytuł"        sortKey="title"     current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Typ"          sortKey="type"      current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Ujęcie"       sortKey="intake"    current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Zamawiający"  sortKey="client"    current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Status"       sortKey="status"    current={sort} onClick={onHeaderClick}/>
          <th>Aktualnie u:</th>
          <SortHeader<SortKey> label="Priorytet"    sortKey="priority"  current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Dokumenty"    sortKey="docs"      current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Termin"       sortKey="deadline"  current={sort} onClick={onHeaderClick}/>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {pagedItems.map(o => (
          <tr key={o.id} onClick={() => onRowClick(o.id)} className="cursor-pointer hover:bg-ink-50">
            <td className="font-mono text-[12px] text-ink-500">{o.number}</td>
            <td className="font-semibold text-ink-900">{o.title}</td>
            <td><span className="pill pill-earth">{o.typeAbbreviation}-{o.typeCode}</span></td>
            <td className="text-ink-600">{o.intakeName ?? '—'}</td>
            <td className="text-ink-700 max-w-[220px]">
              {o.clientId ? (
                <NavLink to={`/klienci/${o.clientId}`} onClick={e => e.stopPropagation()}
                         className="hover:text-water-600 hover:underline line-clamp-2" title={o.clientName ?? ''}>
                  {o.clientName}
                </NavLink>
              ) : <span className="text-ink-400">—</span>}
            </td>
            <td><span className={'pill pill-' + (o.statusColor || 'water')}>{o.statusName}</span></td>
            <td>
              {o.currentStepCode ? (
                <div className="flex items-center gap-1.5 text-[11px]">
                  {o.currentAssigneeInitials && (
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ink-100 text-[9px] font-bold text-ink-700 shrink-0"
                          title={o.currentAssigneeFullName ?? ''}>
                      {o.currentAssigneeInitials}
                    </span>
                  )}
                  <div className="min-w-0">
                    <div className={o.currentAssigneeFullName ? 'text-ink-700 truncate max-w-[180px]' : 'text-ink-400 italic'}>
                      {o.currentAssigneeFullName ?? 'nieprzypisane'}
                    </div>
                    <div className="text-[10px] text-ink-500 truncate max-w-[180px]" title={(o.currentStepCode + ' · ' + (o.currentStepName ?? ''))}>
                      <span className="font-mono text-ink-400">{o.currentStepCode}</span>
                      {o.currentStepName && <span> · {o.currentStepName}</span>}
                      {o.currentStepStatus === 'IN_PROGRESS' && <span className="text-amber-600"> · w toku</span>}
                    </div>
                  </div>
                </div>
              ) : <span className="text-ink-400 text-[11px]">—</span>}
            </td>
            <td>
              {priorytetPill(o.priority)
                ? <span className={'pill ' + priorytetPill(o.priority)}>{o.priorityName}</span>
                : <span className="text-ink-500 text-[12px]">{o.priorityName}</span>}
            </td>
            <td><RequiredDocsCell summary={o}/></td>
            <td className={'font-mono text-[12px] ' + terminColor(o.deadline)}>
              {o.deadline ? (
                <div className="flex flex-col leading-tight">
                  <span>{o.deadline}</span>
                  <span className="text-[10px] font-sans">{terminLabel(o.deadline)}</span>
                </div>
              ) : '—'}
            </td>
            <td><Icon name="chevron" className="text-ink-300"/></td>
          </tr>
        ))}
      </tbody>
    </table>
    <Pagination total={total} page={page} pageSize={pageSize}
                onPageChange={setPage} onPageSizeChange={setPageSize}/>
    </>
  );
}
